/**
 * CreateGroup — cover/avatar upload, name, description and privacy, submitting
 * through the shared groupService (Figma 6707-22654; mobile createGroup parity).
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import {
  CameraIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  PostImageIcon,
} from '../components/ui/icons';
import { GroupPrivacyModal } from '../features/groups/GroupPrivacyModal';
import { useCreateGroupForm } from '../features/groups/useCreateGroupForm';
import type { GroupImageSlot } from '../features/groups/useGroupImagePicker';
import '../features/groups/groups.css';

export function CreateGroup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    groupName,
    description,
    privacy,
    coverImage,
    avatarImage,
    errors,
    loading,
    setGroupName,
    setDescription,
    setPrivacy,
    setImage,
    submit,
  } = useCreateGroupForm();

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeSlot = useRef<GroupImageSlot>('cover');

  const openPicker = (slot: GroupImageSlot) => {
    activeSlot.current = slot;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) setImage(activeSlot.current, file);
  };

  const privacyLabel = privacy
    ? t(`home.createGroupForm.${privacy.toLowerCase()}`)
    : t('home.createGroupForm.choosePrivacy');

  return (
    <section className="create-group" aria-labelledby="create-group-title">
      <header className="create-group__header">
        <button type="button" className="create-group__back" onClick={() => navigate(-1)}>
          <ChevronLeftIcon size={20} />
          <span id="create-group-title">{t('home.createGroupForm.title')}</span>
        </button>
      </header>

      <form
        className="create-group__form"
        onSubmit={event => { event.preventDefault(); submit().catch(() => undefined); }}
      >
        <div className="create-group__media">
          <button
            type="button"
            className="create-group__cover"
            onClick={() => openPicker('cover')}
            aria-label={t('home.createGroupForm.coverPhoto')}
          >
            {coverImage ? (
              <img className="create-group__cover-image" src={coverImage.previewUrl} alt="" aria-hidden />
            ) : (
              <span className="create-group__cover-placeholder" aria-hidden>
                <PostImageIcon size={56} />
              </span>
            )}
            <span className="create-group__cover-badge" aria-hidden><CameraIcon size={18} /></span>
          </button>
          <button
            type="button"
            className="create-group__avatar"
            onClick={() => openPicker('avatar')}
            aria-label={t('home.createGroupForm.profilePhoto')}
          >
            {avatarImage && (
              <img className="create-group__avatar-image" src={avatarImage.previewUrl} alt="" aria-hidden />
            )}
            <span className="create-group__avatar-badge" aria-hidden><CameraIcon size={14} /></span>
          </button>
          <input
            ref={fileInputRef}
            className="create-group__file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileChange}
          />
        </div>

        <div className="create-group__field">
          <label className="create-group__label" htmlFor="create-group-name">
            {t('home.createGroupForm.groupName')}
          </label>
          <input
            id="create-group-name"
            className={`create-group__input${errors.groupName ? ' create-group__input--error' : ''}`}
            value={groupName}
            placeholder={t('home.createGroupForm.groupNamePlaceholder')}
            aria-invalid={Boolean(errors.groupName)}
            aria-describedby={errors.groupName ? 'create-group-name-error' : undefined}
            onChange={event => setGroupName(event.target.value)}
          />
          <FormError id="create-group-name-error" message={errors.groupName} />
        </div>

        <div className="create-group__field">
          <label className="create-group__label" htmlFor="create-group-description">
            {t('home.createGroupForm.description')}
          </label>
          <textarea
            id="create-group-description"
            className={`create-group__textarea${errors.description ? ' create-group__textarea--error' : ''}`}
            value={description}
            placeholder={t('home.createGroupForm.descriptionPlaceholder')}
            aria-invalid={Boolean(errors.description)}
            aria-describedby={errors.description ? 'create-group-description-error' : undefined}
            onChange={event => setDescription(event.target.value)}
          />
          <FormError id="create-group-description-error" message={errors.description} />
        </div>

        <div className="create-group__field">
          <span className="create-group__label" id="create-group-privacy-label">
            {t('home.createGroupForm.privacy')}
          </span>
          <button
            type="button"
            className={`create-group__dropdown${privacy ? '' : ' create-group__dropdown--placeholder'}${errors.privacy ? ' create-group__dropdown--error' : ''}`}
            onClick={() => setPrivacyOpen(true)}
            aria-labelledby="create-group-privacy-label"
            aria-haspopup="dialog"
            aria-expanded={privacyOpen}
          >
            {privacyLabel}
            <ChevronDownIcon size={20} />
          </button>
          <FormError message={errors.privacy} />
        </div>

        <Button type="submit" size="lg" fullWidth disabled={loading}>
          {t(loading ? 'common.loading' : 'home.createGroupForm.submitButton')}
        </Button>
      </form>

      {privacyOpen && (
        <GroupPrivacyModal
          selected={privacy}
          onClose={() => setPrivacyOpen(false)}
          onSelect={setPrivacy}
        />
      )}
    </section>
  );
}
